import { createFileRoute } from '@tanstack/react-router';
import type { AppEmbed as AppEmbedType } from '@thoughtspot/visual-embed-sdk';
import { AppEmbed, useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { useUserAppViewConfig } from '@/api/queries/useSdkTokenQuery';

interface RouteParams {
  object_id: string;
}

interface ParsedParams {
  object_id: string;
}

/**
 * DEEP LINK ROUTE: AI Highlight Viewer
 *
 * This route serves as a direct access point for sharing/bookmarking specific ThoughtSpot AI highlights.
 * Users typically land here via:
 * - Email links containing AI highlight URLs
 * - Shared links (Slack, Teams, etc.)
 * - Bookmarks
 * - External integrations
 *
 * URL Pattern: /dashboard/ai-highlight/{object_id}
 * Example: /dashboard/ai-highlight/abc-123-def-456
 *
 * This route uses a direct AppEmbed component with its own independent iframe,
 * not the prerendered shared iframe from PrerenderedThoughtSpotProvider.
 */
export const Route = createFileRoute('/dashboard/_deep-links/ai-highlight/$object_id')<{
  params: RouteParams;
}>({
  component: RouteComponent,
  params: {
    parse: (params: Record<string, string>): ParsedParams => {
      const { object_id } = params;

      return {
        object_id,
      };
    },
  },
});

function RouteComponent() {
  const { object_id } = Route.useParams();
  const embedRef = useEmbedRef() as React.RefObject<AppEmbedType>;
  const userAppViewConfig = useUserAppViewConfig();

  return (
    <AppEmbed
      className="h-[calc(100vh-var(--dashboard-menu-height))] w-full"
      ref={embedRef}
      liveboardV2={true}
      enableAskSage={true}
      modularHomeExperience={true}
      dataPanelV2={true}
      // TODO: waiting for TS support verification whether the flag also needs to be explicitly passed on those deep-link routes
      // enableCustomColumnGroups={true}
      // additionalFlags={{ enableCustomColumnGroups: true }}
      path={`insight/${object_id}`}
      {...(userAppViewConfig?.hiddenActions && {
        hiddenActions: userAppViewConfig.hiddenActions,
      })}
    ></AppEmbed>
  );
}
